/**
 * Round 115 (Phase E1 flagship) — Sales Page Builder.
 *
 * This page is the reference implementation for the Universal
 * Builder Standard:
 *
 *   1. Never shows a blank page. On mount it auto-generates a
 *      first draft using Business Brain + Brand Brain + Market
 *      Intelligence.
 *   2. Displays the 7-step BuilderProgressOverlay while generating.
 *   3. Applies Brand Brain colors and fonts to the live preview
 *      instantly via CSS variables.
 *   4. Shows a "Reused from X" badge on any section that was
 *      composed from an approved asset.
 *   5. Any section can be regenerated with one click.
 *   6. The customer edits AFTER the AI has finished — never before.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowClockwise, House, Sparkle, FloppyDisk } from '@phosphor-icons/react';
import api, { salesPageAPI } from '@/utils/api';
import BuilderProgressOverlay from '@/components/BuilderProgressOverlay';

const SalesPageBuilderPage = () => {
  const navigate = useNavigate();
  const [salesPage, setSalesPage] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [regenerating, setRegenerating] = useState('');
  const [dirty, setDirty] = useState(false);
  const [gateChecked, setGateChecked] = useState(false);

  // Round 218A P0 FINAL (2026-02-07) — DIRECT-URL SAFETY.
  // If a customer manually navigates here before completing the
  // foundation, silently redirect to the resolver's next step.
  // Never show a blocker on the primary page — take them to the
  // right place instead.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/foundation/next-action');
        if (cancelled) return;
        const action = res?.data?.action || {};
        if (!action.foundation_complete && action.next_route) {
          toast.info(`${action.next_cta_label || 'Next step'} — I'll take you there.`, { duration: 2600 });
          navigate(action.next_route);
          return;
        }
      } catch (_) {
        // Resolver unavailable — proceed and let the backend refuse
        // if truly ungated.
      } finally {
        if (!cancelled) setGateChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  // Auto-generate a first draft on mount. This is the "AI builds
  // first, user edits second" contract.
  useEffect(() => {
    if (!gateChecked) return;
    let cancelled = false;
    (async () => {
      try {
        // 1. Try to load an existing sales page for the active business.
        const cur = await salesPageAPI.current();
        if (cancelled) return;
        const existing = cur?.sales_page;
        if (existing && existing.status === 'draft' && existing.sections && Object.keys(existing.sections).length > 0) {
          setSalesPage(existing);
          return;
        }
        // 2. No existing draft → auto-generate one.
        setGenerating(true);
        const gen = await salesPageAPI.generate();
        if (cancelled) return;
        setSalesPage(gen?.sales_page || null);
        toast.success('First draft ready. Every section is editable.');
      } catch (err) {
        console.error(err);
        if (!cancelled) toast.error('Could not build a first draft. You can retry with Regenerate.');
      } finally {
        if (!cancelled) setGenerating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [gateChecked]);

  const brandTokens = salesPage?.brand_tokens || {};
  const sections = salesPage?.sections || {};
  const reused = salesPage?.reused_from || {};
  const missing = salesPage?.missing_fields || [];

  const styleVars = useMemo(() => ({
    '--sp-primary': brandTokens.primary || '#0F172A',
    '--sp-primary-alt': brandTokens.primary_alt || '#22C55E',
    '--sp-accent': brandTokens.accent || '#F5F5F4',
    '--sp-heading-font': brandTokens.heading_font
      ? `"${brandTokens.heading_font}", system-ui, sans-serif`
      : 'system-ui, sans-serif',
    '--sp-body-font': brandTokens.body_font
      ? `"${brandTokens.body_font}", system-ui, sans-serif`
      : 'system-ui, sans-serif',
  }), [brandTokens]);

  const updateSection = (name, updater) => {
    setSalesPage((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      const curSections = { ...(next.sections || {}) };
      curSections[name] = typeof updater === 'function'
        ? updater(curSections[name] || {})
        : updater;
      next.sections = curSections;
      return next;
    });
    setDirty(true);
  };

  const savePatch = async () => {
    try {
      const r = await salesPageAPI.patch({ sections });
      setSalesPage(r?.sales_page || salesPage);
      setDirty(false);
      toast.success('Saved.');
    } catch (err) {
      console.error(err);
      toast.error('Save failed.');
    }
  };

  const regenerateSection = async (sectionName) => {
    setRegenerating(sectionName);
    try {
      const r = await salesPageAPI.regenerateSection(sectionName);
      setSalesPage(r?.sales_page || salesPage);
      toast.success(`Regenerated ${sectionName}.`);
    } catch (err) {
      console.error(err);
      toast.error(`Failed to regenerate ${sectionName}.`);
    } finally {
      setRegenerating('');
    }
  };

  return (
    <div
      className="min-h-screen bg-slate-950 text-slate-100"
      style={styleVars}
      data-testid="sales-page-builder"
    >
      <BuilderProgressOverlay visible={generating} title="Building your sales page…" />

      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur px-4 sm:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="sales-page-home"
            className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200"
          >
            <House size={16} /> Dashboard
          </button>
          <div className="text-slate-600">/</div>
          <div className="text-sm font-semibold text-slate-200">Sales Page Builder</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={savePatch}
            disabled={!dirty}
            data-testid="sales-page-save"
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FloppyDisk size={14} /> Save
          </button>
        </div>
      </header>

      {!salesPage && !generating && (
        <div className="p-8 text-slate-400 text-sm">Preparing your first draft…</div>
      )}

      {salesPage && !generating && (
        <div className="grid lg:grid-cols-[420px_1fr] gap-0 min-h-[calc(100vh-4rem)]">
          {/* Editor pane */}
          <aside className="border-r border-slate-800 bg-slate-900/40 p-6 space-y-6 overflow-y-auto">
            {missing.length > 0 && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
                <div className="font-semibold mb-1 flex items-center gap-1"><Sparkle size={12} weight="fill" /> {missing.length} field{missing.length === 1 ? '' : 's'} used a placeholder</div>
                <div className="text-amber-300/80">Improve later from Settings → Business Brain. Missing: {missing.slice(0, 3).join(', ')}{missing.length > 3 ? '…' : ''}</div>
              </div>
            )}

            {['hero', 'about', 'services', 'testimonials', 'cta'].map((name) => (
              <SectionEditor
                key={name}
                name={name}
                data={sections[name]}
                reusedFrom={reused[name]}
                busy={regenerating === name || generating}
                onChange={(next) => updateSection(name, next)}
                onRegenerate={() => regenerateSection(name)}
              />
            ))}
          </aside>

          {/* Live preview pane */}
          <main className="p-6 sm:p-10 overflow-y-auto">
            <div
              className="mx-auto bg-white text-slate-900 rounded-xl shadow-xl overflow-hidden"
              style={{ maxWidth: 900 }}
              data-testid="sales-page-preview"
            >
              <PreviewHero data={sections.hero} tokens={brandTokens} />
              <PreviewAbout data={sections.about} tokens={brandTokens} />
              <PreviewServices data={sections.services} tokens={brandTokens} />
              <PreviewTestimonials data={sections.testimonials} tokens={brandTokens} />
              <PreviewCta data={sections.cta} tokens={brandTokens} />
            </div>
          </main>
        </div>
      )}
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────
// Editor panels
// ─────────────────────────────────────────────────────────────────
const SectionEditor = ({ name, data, reusedFrom, busy, onChange, onRegenerate }) => {
  const label = name.charAt(0).toUpperCase() + name.slice(1);
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4" data-testid={`editor-section-${name}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-slate-100">{label}</div>
          {reusedFrom && (
            <div
              data-testid={`reused-badge-${name}`}
              className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 text-[10px] uppercase tracking-widest text-emerald-300"
            >
              <Sparkle size={9} weight="fill" /> Reused from {reusedFrom.asset_type || reusedFrom.source_asset_type || 'approved asset'}
            </div>
          )}
        </div>
        <button
          onClick={onRegenerate}
          disabled={busy}
          data-testid={`regenerate-${name}`}
          className="inline-flex items-center gap-1 rounded-md border border-slate-700 bg-slate-800/60 px-2 py-1 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-40"
        >
          <ArrowClockwise size={12} className={busy ? 'animate-spin' : ''} /> Regenerate
        </button>
      </div>

      {name === 'hero' && (
        <div className="space-y-2">
          <TextField label="Headline" value={data?.headline || ''} onChange={(v) => onChange({ ...data, headline: v })} testid={`hero-headline-input`} />
          <TextArea  label="Subheadline" value={data?.subheadline || ''} onChange={(v) => onChange({ ...data, subheadline: v })} testid={`hero-subheadline-input`} />
          <TextField label="CTA button copy" value={data?.cta || ''} onChange={(v) => onChange({ ...data, cta: v })} testid={`hero-cta-input`} />
        </div>
      )}
      {name === 'about' && (
        <div className="space-y-2">
          <TextField label="Heading" value={data?.heading || ''} onChange={(v) => onChange({ ...data, heading: v })} testid={`about-heading-input`} />
          <TextArea  label="Body" value={data?.body || ''} onChange={(v) => onChange({ ...data, body: v })} testid={`about-body-input`} rows={4} />
        </div>
      )}
      {name === 'services' && (
        <div className="space-y-2">
          <TextField label="Heading" value={data?.heading || ''} onChange={(v) => onChange({ ...data, heading: v })} testid={`services-heading-input`} />
          {(data?.items || []).map((item, idx) => (
            <div key={idx} className="rounded-lg border border-slate-800 p-2 space-y-1">
              <TextField label={`Item ${idx + 1} title`} value={item.title || ''} onChange={(v) => {
                const items = [...(data?.items || [])];
                items[idx] = { ...items[idx], title: v };
                onChange({ ...data, items });
              }} testid={`services-item-${idx}-title`} />
              <TextArea label="Body" value={item.body || ''} onChange={(v) => {
                const items = [...(data?.items || [])];
                items[idx] = { ...items[idx], body: v };
                onChange({ ...data, items });
              }} testid={`services-item-${idx}-body`} rows={2} />
            </div>
          ))}
        </div>
      )}
      {name === 'testimonials' && (
        <div className="space-y-2">
          <TextField label="Heading" value={data?.heading || ''} onChange={(v) => onChange({ ...data, heading: v })} testid={`testimonials-heading-input`} />
          {(data?.items || []).map((item, idx) => (
            <div key={idx} className="rounded-lg border border-slate-800 p-2 space-y-1">
              <TextArea label={`Quote ${idx + 1}`} value={item.quote || ''} onChange={(v) => {
                const items = [...(data?.items || [])];
                items[idx] = { ...items[idx], quote: v };
                onChange({ ...data, items });
              }} testid={`test-quote-${idx}`} rows={2} />
              <TextField label="Author" value={item.author || ''} onChange={(v) => {
                const items = [...(data?.items || [])];
                items[idx] = { ...items[idx], author: v };
                onChange({ ...data, items });
              }} testid={`test-author-${idx}`} />
            </div>
          ))}
        </div>
      )}
      {name === 'cta' && (
        <div className="space-y-2">
          <TextField label="Heading" value={data?.heading || ''} onChange={(v) => onChange({ ...data, heading: v })} testid={`cta-heading-input`} />
          <TextArea  label="Body" value={data?.body || ''} onChange={(v) => onChange({ ...data, body: v })} testid={`cta-body-input`} rows={3} />
          <TextField label="Button label" value={data?.button || ''} onChange={(v) => onChange({ ...data, button: v })} testid={`cta-button-input`} />
        </div>
      )}
    </div>
  );
};

const TextField = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-sm text-slate-100 outline-none focus:border-emerald-400"
    />
  </div>
);

const TextArea = ({ label, value, onChange, rows = 2, testid }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      data-testid={testid}
      className="w-full rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-sm text-slate-100 outline-none focus:border-emerald-400 resize-y"
    />
  </div>
);


// ─────────────────────────────────────────────────────────────────
// Preview components — all styled with CSS variables from Brand Brain
// ─────────────────────────────────────────────────────────────────
const PreviewHero = ({ data, tokens }) => (
  <section className="px-10 py-16 text-center" style={{ background: 'var(--sp-primary)' }} data-testid="preview-hero">
    {tokens?.logo_url && (
      <img src={tokens.logo_url} alt="" className="mx-auto h-14 mb-6 object-contain" />
    )}
    <h1 className="text-4xl font-bold text-white mb-3" style={{ fontFamily: 'var(--sp-heading-font)' }}>
      {data?.headline || 'Your Headline Here'}
    </h1>
    <p className="text-white/85 text-lg max-w-2xl mx-auto mb-8" style={{ fontFamily: 'var(--sp-body-font)' }}>
      {data?.subheadline || 'A short subheadline goes here.'}
    </p>
    <button
      className="rounded-lg px-6 py-3 text-base font-semibold"
      style={{ background: 'var(--sp-primary-alt)', color: '#fff', fontFamily: 'var(--sp-body-font)' }}
    >
      {data?.cta || 'Get Started'}
    </button>
  </section>
);

const PreviewAbout = ({ data, tokens }) => (
  <section className="px-10 py-14 bg-white" data-testid="preview-about">
    <h2 className="text-2xl font-bold mb-3" style={{ color: 'var(--sp-primary)', fontFamily: 'var(--sp-heading-font)' }}>
      {data?.heading || 'About'}
    </h2>
    <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'var(--sp-body-font)' }}>
      {data?.body || 'Your about copy goes here.'}
    </p>
  </section>
);

const PreviewServices = ({ data, tokens }) => (
  <section className="px-10 py-14" style={{ background: 'var(--sp-accent)' }} data-testid="preview-services">
    <h2 className="text-2xl font-bold mb-6 text-center" style={{ color: 'var(--sp-primary)', fontFamily: 'var(--sp-heading-font)' }}>
      {data?.heading || 'Services'}
    </h2>
    <div className="grid sm:grid-cols-3 gap-4">
      {(data?.items || []).map((item, idx) => (
        <div key={idx} className="rounded-xl bg-white p-5 shadow">
          <h3 className="font-semibold mb-2" style={{ color: 'var(--sp-primary)', fontFamily: 'var(--sp-heading-font)' }}>
            {item.title}
          </h3>
          <p className="text-sm text-slate-600" style={{ fontFamily: 'var(--sp-body-font)' }}>{item.body}</p>
        </div>
      ))}
    </div>
  </section>
);

const PreviewTestimonials = ({ data, tokens }) => (
  <section className="px-10 py-14 bg-white" data-testid="preview-testimonials">
    <h2 className="text-2xl font-bold mb-6 text-center" style={{ color: 'var(--sp-primary)', fontFamily: 'var(--sp-heading-font)' }}>
      {data?.heading || 'What people say'}
    </h2>
    <div className="grid sm:grid-cols-2 gap-6">
      {(data?.items || []).map((item, idx) => (
        <div key={idx} className="rounded-xl border border-slate-200 p-6">
          <p className="text-slate-700 italic mb-3" style={{ fontFamily: 'var(--sp-body-font)' }}>
            "{item.quote}"
          </p>
          <div className="text-sm text-slate-500">— {item.author}</div>
        </div>
      ))}
    </div>
  </section>
);

const PreviewCta = ({ data, tokens }) => (
  <section className="px-10 py-16 text-center" style={{ background: 'var(--sp-primary)' }} data-testid="preview-cta">
    <h2 className="text-3xl font-bold text-white mb-3" style={{ fontFamily: 'var(--sp-heading-font)' }}>
      {data?.heading || 'Ready to get started?'}
    </h2>
    <p className="text-white/85 max-w-2xl mx-auto mb-6" style={{ fontFamily: 'var(--sp-body-font)' }}>
      {data?.body || ''}
    </p>
    <button
      className="rounded-lg px-6 py-3 text-base font-semibold"
      style={{ background: 'var(--sp-primary-alt)', color: '#fff', fontFamily: 'var(--sp-body-font)' }}
    >
      {data?.button || 'Get Started'}
    </button>
  </section>
);


export default SalesPageBuilderPage;
